import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { logoutAdmin } from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  // Auth is driven by EITHER the httpOnly `admin_token` cookie OR a legacy localStorage
  // Bearer token. On production behind some CDNs (e.g. Cloudflare with cookie stripping)
  // the Set-Cookie header from the login response never reaches the browser — so we
  // also persist the `access_token` from the login response body to localStorage as a
  // resilient Bearer fallback. The backend accepts either source.
  const checkAuth = async () => {
    const legacyToken = localStorage.getItem('admin_token');

    try {
      const response = await axios.get(`${API}/admin/verify`, {
        withCredentials: true,
        ...(legacyToken && { headers: { Authorization: `Bearer ${legacyToken}` } }),
      });
      setIsAuthenticated(true);
      setUser(response.data.user);
      // NOTE: keep the legacyToken in localStorage. If the cookie did get set,
      // future requests will use it; if the cookie was stripped by a proxy,
      // the Bearer header still works.
    } catch (error) {
      localStorage.removeItem('admin_token');
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = useCallback(async (username, password, recaptchaToken = null) => {
    // Server sets the httpOnly cookie AND returns the token in the body. We persist
    // the body token to localStorage as a Bearer fallback for CDN/proxy setups that
    // strip Set-Cookie.
    const response = await axios.post(
      `${API}/admin/login`,
      { username, password, recaptchaToken },
      { withCredentials: true },
    );
    if (response.data?.access_token) {
      localStorage.setItem('admin_token', response.data.access_token);
    }
    setIsAuthenticated(true);
    setUser(response.data.user);
    return response.data;
  }, []);

  const register = useCallback(async (username, password, recaptchaToken = null) => {
    const response = await axios.post(
      `${API}/admin/register`,
      { username, password, recaptchaToken },
      { withCredentials: true },
    );
    if (response.data?.access_token) {
      localStorage.setItem('admin_token', response.data.access_token);
    }
    setIsAuthenticated(true);
    setUser(response.data.user);
    return response.data;
  }, []);

  const logout = useCallback(async () => {
    // Best-effort server-side cookie clear; never throw if backend is unreachable
    try {
      await logoutAdmin();
    } catch {
      /* ignore — we still want to drop client state */
    }
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  // Helper to check if user can access a module
  const canAccessModule = useCallback((module) => {
    if (!user || !user.permissions) return false;
    return user.permissions[module] === true;
  }, [user]);

  // Helper to check if user can access a church
  const canAccessChurch = useCallback((church) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    if (!user.assigned_churches || user.assigned_churches.length === 0) {
      return user.role === 'super_admin';
    }
    return user.assigned_churches.includes(church);
  }, [user]);

  // For backwards compatibility
  const username = user?.username || null;

  // Memoize the context value so React.memo'd consumers don't re-render needlessly
  const contextValue = useMemo(() => ({
    isAuthenticated,
    user,
    username,
    loading,
    login,
    register,
    logout,
    canAccessModule,
    canAccessChurch,
  }), [isAuthenticated, user, username, loading, login, register, logout, canAccessModule, canAccessChurch]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
